import React, { useState, Component } from 'react';
import { XIcon, ClockIcon, PhoneIcon, AlertCircleIcon, SmileIcon, CopyIcon, MessageSquareIcon, CalendarIcon, PackageIcon, TargetIcon, ActivityIcon } from 'lucide-react';
interface Lead {
  id: number;
  name: string;
  initials?: string;
  email: string;
  phone: string;
  callDuration?: string;
  leadType: 'cold' | 'warm' | 'hot';
  status: string;
  dealerCode?: string;
  dealerName?: string;
  carModel?: string;
}
interface LeadDetailOverlayProps {
  isOpen: boolean;
  onClose: () => void;
  lead: Lead | null;
}
// AI Icon Component
function AIIcon({
  className = ''
}: {
  className?: string;
}) {
  return <div className={`w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center ${className}`} data-id="element-417">
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" className="text-blue-600" data-id="element-418">
        <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" fill="currentColor" data-id="element-419" />
      </svg>
    </div>;
}
// User Avatar Component
function UserAvatar({
  initials,
  className = ''
}: {
  initials: string;
  className?: string;
}) {
  return <div className={`w-10 h-10 bg-gray-800 rounded-lg flex items-center justify-center text-white text-sm font-bold ${className}`} data-id="element-420">
      {initials}
    </div>;
}
export function LeadDetailOverlay({
  isOpen,
  onClose,
  lead
}: LeadDetailOverlayProps) {
  const [activeTab, setActiveTab] = useState<'details' | 'ai-analytics'>('details');
  const [leadStatus, setLeadStatus] = useState('buy-mahindra-car');
  const [buyStatus, setBuyStatus] = useState('');
  const [notes, setNotes] = useState('');
  const [selectedModel, setSelectedModel] = useState('');
  if (!isOpen || !lead) return null;
  const userInitials = lead.initials || lead.name.substring(0, 2).toUpperCase();
  return <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/20 z-40 transition-opacity duration-300" onClick={onClose} data-id="element-421" />

      {/* Overlay Panel */}
      <div className="fixed right-0 top-0 bottom-0 w-[480px] bg-white shadow-2xl z-50 transform transition-transform duration-300 ease-out flex flex-col" data-id="element-422">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200" data-id="element-423">
          <div className="flex items-center gap-2" data-id="element-424">
            <div className="w-8 h-8 bg-blue-600 rounded flex items-center justify-center" data-id="element-425">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" data-id="element-426">
                <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" data-id="element-427" />
              </svg>
            </div>
            <h2 className="text-lg font-semibold text-gray-900" data-id="element-428">
              Lead Details
            </h2>
          </div>
          <div className="flex items-center gap-3" data-id="element-429">
            <button className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-2" data-id="element-430">
              <ClockIcon size={16} data-id="element-431" />
              ACTIVITY LOGS
            </button>
            <button onClick={onClose} className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 transition-colors" data-id="element-432">
              <XIcon size={20} data-id="element-433" />
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-200 px-6" data-id="element-434">
          <button onClick={() => setActiveTab('details')} className={`px-4 py-3 text-sm font-semibold border-b-2 transition-colors ${activeTab === 'details' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`} data-id="element-435">
            DETAILS
          </button>
          <button onClick={() => setActiveTab('ai-analytics')} className={`px-4 py-3 text-sm font-semibold border-b-2 transition-colors ${activeTab === 'ai-analytics' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`} data-id="element-436">
            AI ANALYTICS
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-6" data-id="element-437">
          {activeTab === 'details' && <div className="space-y-5" data-id="element-438">
              {/* Status Banner */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-3 flex items-center gap-2 text-sm text-blue-700" data-id="element-439">
                <PhoneIcon size={16} data-id="element-440" />
                <span className="font-medium" data-id="element-441">
                  Call routed to dealer and answered.
                </span>
              </div>

              {/* Name and Email */}
              <div className="grid grid-cols-2 gap-4" data-id="element-442">
                <div data-id="element-443">
                  <label className="block text-xs font-medium text-gray-700 mb-2" data-id="element-444">
                    Name
                  </label>
                  <input type="text" value={lead.name} readOnly className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-900" data-id="element-445" />
                </div>
                <div data-id="element-446">
                  <label className="block text-xs font-medium text-gray-700 mb-2" data-id="element-447">
                    Email
                  </label>
                  <input type="email" value={lead.email} readOnly className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-900" data-id="element-448" />
                </div>
              </div>

              {/* Phone Number, Call Duration, Lead Type */}
              <div className="grid grid-cols-3 gap-4" data-id="element-449">
                <div data-id="element-450">
                  <label className="block text-xs font-medium text-gray-700 mb-2" data-id="element-451">
                    Phone Number
                  </label>
                  <input type="tel" value={lead.phone} readOnly className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-900" data-id="element-452" />
                </div>
                <div data-id="element-453">
                  <label className="block text-xs font-medium text-gray-700 mb-2" data-id="element-454">
                    Call Duration
                  </label>
                  <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg h-[38px]" data-id="element-455">
                    <ClockIcon size={14} className="text-blue-600" data-id="element-456" />
                    <span className="text-sm font-semibold text-blue-600" data-id="element-457">
                      {lead.callDuration || '12:21 Sec'}
                    </span>
                  </div>
                </div>
                <div data-id="element-458">
                  <label className="block text-xs font-medium text-gray-700 mb-2" data-id="element-459">
                    Lead Type
                  </label>
                  <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg h-[38px]" data-id="element-460">
                    <div className="w-4 h-4 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0" data-id="element-461">
                      <svg width="10" height="10" viewBox="0 0 10 10" fill="none" data-id="element-462">
                        <path d="M2 5L4 7L8 3" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-463" />
                      </svg>
                    </div>
                    <span className="text-sm font-medium text-blue-600" data-id="element-464">
                      Cold
                    </span>
                  </div>
                </div>
              </div>

              {/* Lead Status Radio Buttons */}
              <div data-id="element-465">
                <label className="block text-xs font-medium text-gray-700 mb-3" data-id="element-466">
                  Lead Status
                </label>
                <div className="grid grid-cols-2 gap-3" data-id="element-467">
                  <label className="flex items-center gap-2 cursor-pointer" data-id="element-468">
                    <input type="radio" name="leadStatus" value="buy-mahindra-car" checked={leadStatus === 'buy-mahindra-car'} onChange={e => setLeadStatus(e.target.value)} className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500" data-id="element-469" />
                    <span className="text-sm text-gray-700 font-medium" data-id="element-470">
                      BUY A MAHINDRA CAR
                    </span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer" data-id="element-471">
                    <input type="radio" name="leadStatus" value="service-mahindra-car" checked={leadStatus === 'service-mahindra-car'} onChange={e => setLeadStatus(e.target.value)} className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500" data-id="element-472" />
                    <span className="text-sm text-gray-500" data-id="element-473">
                      SERVICE MAHINDRA CAR
                    </span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer" data-id="element-474">
                    <input type="radio" name="leadStatus" value="feedback" checked={leadStatus === 'feedback'} onChange={e => setLeadStatus(e.target.value)} className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500" data-id="element-475" />
                    <span className="text-sm text-gray-500" data-id="element-476">FEEDBACK</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer" data-id="element-477">
                    <input type="radio" name="leadStatus" value="non-sale" checked={leadStatus === 'non-sale'} onChange={e => setLeadStatus(e.target.value)} className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500" data-id="element-478" />
                    <span className="text-sm text-gray-500" data-id="element-479">NON SALE</span>
                  </label>
                </div>
              </div>

              {/* Buy A Mahindra Car Dropdown */}
              <div data-id="element-480">
                <label className="block text-xs font-medium text-gray-700 mb-2" data-id="element-481">
                  Buy A Mahindra Car
                </label>
                <select value={buyStatus} onChange={e => setBuyStatus(e.target.value)} className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-sm text-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 appearance-none bg-[url('data:image/svg+xml;charset=UTF-8,%3csvg xmlns=%27http://www.w3.org/2000/svg%27 viewBox=%270 0 24 24%27 fill=%27none%27 stroke=%27currentColor%27 stroke-width=%272%27 stroke-linecap=%27round%27 stroke-linejoin=%27round%27%3e%3cpolyline points=%276 9 12 15 18 9%27%3e%3c/polyline%3e%3c/svg%3e')] bg-[length:20px] bg-[right_0.5rem_center] bg-no-repeat pr-10" data-id="element-482">
                  <option value="" data-id="element-483">Select Status</option>
                  <option value="interested" data-id="element-484">Interested</option>
                  <option value="test-drive" data-id="element-485">Test Drive Scheduled</option>
                  <option value="negotiating" data-id="element-486">Negotiating</option>
                  <option value="closed" data-id="element-487">Closed</option>
                </select>
              </div>

              {/* Notes */}
              <div data-id="element-488">
                <label className="block text-xs font-medium text-gray-700 mb-2" data-id="element-489">
                  Notes
                </label>
                <textarea value={notes} onChange={e => setNotes(e.target.value)} placeholder="Enter Details" rows={3} className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-sm text-gray-700 placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none" data-id="element-490" />
              </div>

              {/* Sync State Progress */}
              <div className="bg-gray-50 rounded-lg p-4" data-id="element-491">
                <label className="block text-sm font-semibold text-gray-700 mb-4" data-id="element-492">
                  Sync State
                </label>
                <div className="space-y-3" data-id="element-493">
                  <div className="flex items-center justify-between text-[11px] font-semibold" data-id="element-494">
                    <span className="text-gray-600" data-id="element-495">CREATED</span>
                    <span className="text-gray-600" data-id="element-496">OPENED</span>
                    <span className="text-gray-600" data-id="element-497">SAVED</span>
                    <span className="text-blue-600" data-id="element-498">SYNCED</span>
                    <span className="text-gray-400" data-id="element-499">ENQUIRY</span>
                  </div>
                  <div className="flex items-center" data-id="element-500">
                    <div className="flex-1 relative" data-id="element-501">
                      <div className="h-3 bg-blue-600 rounded-l-full" style={{
                    clipPath: 'polygon(0 0, calc(100% - 8px) 0, 100% 50%, calc(100% - 8px) 100%, 0 100%)'
                  }} data-id="element-502"></div>
                    </div>
                    <div className="flex-1 relative -ml-1" data-id="element-503">
                      <div className="h-3 bg-blue-600" style={{
                    clipPath: 'polygon(0 0, calc(100% - 8px) 0, 100% 50%, calc(100% - 8px) 100%, 0 100%, 8px 50%)'
                  }} data-id="element-504"></div>
                    </div>
                    <div className="flex-1 relative -ml-1" data-id="element-505">
                      <div className="h-3 bg-blue-600" style={{
                    clipPath: 'polygon(0 0, calc(100% - 8px) 0, 100% 50%, calc(100% - 8px) 100%, 0 100%, 8px 50%)'
                  }} data-id="element-506"></div>
                    </div>
                    <div className="flex-1 relative -ml-1" data-id="element-507">
                      <div className="h-3 bg-blue-800" style={{
                    clipPath: 'polygon(0 0, calc(100% - 8px) 0, 100% 50%, calc(100% - 8px) 100%, 0 100%, 8px 50%)'
                  }} data-id="element-508"></div>
                    </div>
                    <div className="flex-1 relative -ml-1" data-id="element-509">
                      <div className="h-3 bg-gray-300 rounded-r-full" style={{
                    clipPath: 'polygon(0 0, 100% 0, 100% 100%, 0 100%, 8px 50%)'
                  }} data-id="element-510"></div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-amber-600" data-id="element-511">
                    <AlertCircleIcon size={14} data-id="element-512" />
                    <span data-id="element-513">Syncing Failed on 15 Nov 2024</span>
                  </div>
                </div>
              </div>

              {/* Model Dropdown */}
              <div data-id="element-514">
                <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-515">
                  Model
                </label>
                <select value={selectedModel} onChange={e => setSelectedModel(e.target.value)} className="w-full px-3 py-3 bg-white border border-gray-300 rounded-lg text-sm text-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 appearance-none bg-[url('data:image/svg+xml;charset=UTF-8,%3csvg xmlns=%27http://www.w3.org/2000/svg%27 viewBox=%270 0 24 24%27 fill=%27none%27 stroke=%27%239ca3af%27 stroke-width=%272%27 stroke-linecap=%27round%27 stroke-linejoin=%27round%27%3e%3cpolyline points=%276 9 12 15 18 9%27%3e%3c/polyline%3e%3c/svg%3e')] bg-[length:20px] bg-[right_0.75rem_center] bg-no-repeat pr-10" data-id="element-516">
                  <option value="" data-id="element-517">Select model</option>
                  <option value="thar" data-id="element-518">Thar</option>
                  <option value="thar-roxx" data-id="element-519">Thar Roxx</option>
                  <option value="scorpio-n" data-id="element-520">Scorpio-N</option>
                  <option value="xuv700" data-id="element-521">XUV700</option>
                  <option value="xuv400" data-id="element-522">XUV400</option>
                  <option value="bolero" data-id="element-523">Bolero</option>
                  <option value="bolero-neo" data-id="element-524">Bolero Neo</option>
                </select>
              </div>

              {/* Lead Created On */}
              <div data-id="element-525">
                <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-526">
                  Lead Created on
                </label>
                <input type="text" value="15 Nov 2024" readOnly className="w-full px-3 py-3 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-600" data-id="element-527" />
              </div>

              {/* Dealer Code and Dealer Name */}
              <div className="grid grid-cols-2 gap-4" data-id="element-528">
                <div data-id="element-529">
                  <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-530">
                    Dealer Code
                  </label>
                  <input type="text" value={lead.dealerCode || 'HR407'} readOnly className="w-full px-3 py-3 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-600" data-id="element-531" />
                </div>
                <div data-id="element-532">
                  <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-533">
                    Dealer Name
                  </label>
                  <input type="text" value={lead.dealerName || 'Prem Kumar'} readOnly className="w-full px-3 py-3 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-600" data-id="element-534" />
                </div>
              </div>
            </div>}

          {activeTab === 'ai-analytics' && <div className="space-y-6" data-id="element-535">
              {/* Call Back Reason Banner */}
              <div className="bg-orange-50 border-l-4 border-orange-400 rounded-r-lg px-4 py-3" data-id="element-536">
                <div className="flex items-center gap-2 text-orange-600 font-semibold text-sm mb-1" data-id="element-537">
                  <PhoneIcon size={16} data-id="element-538" />
                  CALL BACK REASON
                </div>
                <p className="text-gray-700 text-sm" data-id="element-539">
                  Voicebot agent didn't understand user inputs
                </p>
              </div>

              {/* Call Data Section */}
              <div className="bg-gray-50 rounded-xl p-5" data-id="element-540">
                <div className="flex items-center gap-2 mb-4" data-id="element-541">
                  <ActivityIcon size={18} className="text-blue-600" data-id="element-542" />
                  <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wide" data-id="element-543">
                    CALL DATA
                  </h3>
                </div>

                <div className="grid grid-cols-2 gap-6 mb-5" data-id="element-544">
                  <div className="flex items-center gap-3" data-id="element-545">
                    <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center" data-id="element-546">
                      <ClockIcon size={20} className="text-blue-600" data-id="element-547" />
                    </div>
                    <div data-id="element-548">
                      <p className="text-xs text-gray-500 mb-0.5" data-id="element-549">Duration</p>
                      <p className="text-lg font-bold text-gray-900" data-id="element-550">04:32</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3" data-id="element-551">
                    <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center" data-id="element-552">
                      <SmileIcon size={20} className="text-blue-600" data-id="element-553" />
                    </div>
                    <div data-id="element-554">
                      <p className="text-xs text-gray-500 mb-0.5" data-id="element-555">Sentiment</p>
                      <p className="text-lg font-bold text-green-500" data-id="element-556">
                        POSITIVE
                      </p>
                    </div>
                  </div>
                </div>

                <div data-id="element-557">
                  <div className="flex items-center justify-between mb-2" data-id="element-558">
                    <span className="text-sm text-gray-600" data-id="element-559">
                      Call Completion Status
                    </span>
                    <span className="text-sm font-bold text-green-500" data-id="element-560">
                      100%
                    </span>
                  </div>
                  <div className="h-2 bg-gray-200 rounded-full overflow-hidden" data-id="element-561">
                    <div className="h-full bg-green-500 rounded-full" style={{
                  width: '100%'
                }} data-id="element-562"></div>
                  </div>
                </div>
              </div>

              {/* Call Intent Section */}
              <div className="border-b border-gray-200 pb-6" data-id="element-563">
                <div className="flex items-center gap-2 mb-4" data-id="element-564">
                  <TargetIcon size={18} className="text-blue-600" data-id="element-565" />
                  <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wide" data-id="element-566">
                    CALL INTENT
                  </h3>
                </div>

                <div className="grid grid-cols-2 gap-6" data-id="element-567">
                  <div data-id="element-568">
                    <p className="text-xs text-blue-500 mb-2" data-id="element-569">Reason</p>
                    <span className="inline-block px-4 py-1.5 bg-orange-50 text-orange-600 border border-orange-200 rounded-full text-sm font-medium" data-id="element-570">
                      Purchase Intent
                    </span>
                  </div>
                  <div data-id="element-571">
                    <p className="text-xs text-blue-500 mb-2" data-id="element-572">Purpose</p>
                    <p className="text-sm text-gray-700" data-id="element-573">
                      Inquiring about new SUV models and test drive
                      availability.
                    </p>
                  </div>
                </div>
              </div>

              {/* Product Interest Section */}
              <div className="border-b border-gray-200 pb-6" data-id="element-574">
                <div className="flex items-center gap-2 mb-4" data-id="element-575">
                  <PackageIcon size={18} className="text-blue-600" data-id="element-576" />
                  <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wide" data-id="element-577">
                    PRODUCT INTEREST
                  </h3>
                </div>

                <div className="grid grid-cols-2 gap-6 mb-4" data-id="element-578">
                  <div data-id="element-579">
                    <p className="text-xs text-blue-500 mb-2" data-id="element-580">Product</p>
                    <p className="text-sm font-medium text-gray-900" data-id="element-581">
                      Tata Harrier XZA+
                    </p>
                  </div>
                  <div data-id="element-582">
                    <p className="text-xs text-blue-500 mb-2" data-id="element-583">Budget</p>
                    <p className="text-sm font-medium text-gray-900" data-id="element-584">
                      ₹20L - ₹25L
                    </p>
                  </div>
                </div>

                <div data-id="element-585">
                  <p className="text-xs text-blue-500 mb-2" data-id="element-586">Preferences</p>
                  <div className="flex flex-wrap gap-2" data-id="element-587">
                    {['Dark Edition', 'Automatic', 'Sunroof', 'Diesel'].map(pref => <span key={pref} className="px-3 py-1.5 bg-white border border-gray-300 rounded-full text-sm text-gray-700" data-id="element-588">
                          {pref}
                        </span>)}
                  </div>
                </div>
              </div>

              {/* Timeline / Preferences Section */}
              <div className="border-b border-gray-200 pb-6" data-id="element-589">
                <div className="flex items-center gap-2 mb-4" data-id="element-590">
                  <CalendarIcon size={18} className="text-blue-600" data-id="element-591" />
                  <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wide" data-id="element-592">
                    TIMELINE / PREFERENCES
                  </h3>
                </div>

                <div className="grid grid-cols-2 gap-x-6 gap-y-4" data-id="element-593">
                  <div data-id="element-594">
                    <p className="text-xs text-blue-500 mb-1" data-id="element-595">
                      Expected Purchase
                    </p>
                    <p className="text-sm font-medium text-gray-900" data-id="element-596">
                      Within 2 weeks
                    </p>
                  </div>
                  <div data-id="element-597">
                    <p className="text-xs text-blue-500 mb-1" data-id="element-598">
                      Conversion Score
                    </p>
                    <p className="text-sm font-medium text-gray-900" data-id="element-599">High</p>
                  </div>
                  <div data-id="element-600">
                    <p className="text-xs text-blue-500 mb-1" data-id="element-601">Contact Time</p>
                    <p className="text-sm font-medium text-gray-900" data-id="element-602">
                      After 6:00 PM
                    </p>
                  </div>
                  <div data-id="element-603">
                    <p className="text-xs text-blue-500 mb-1" data-id="element-604">Appointment</p>
                    <p className="text-sm font-medium text-gray-900" data-id="element-605">
                      Scheduled: Sat, 06th Dec, 11:00 AM
                    </p>
                  </div>
                  <div data-id="element-606">
                    <p className="text-xs text-blue-500 mb-1" data-id="element-607">Channel</p>
                    <p className="text-sm font-medium text-gray-900" data-id="element-608">
                      WhatsApp
                    </p>
                  </div>
                </div>
              </div>

              {/* Question / Conversation by Voicebot Agent */}
              <div data-id="element-609">
                <div className="flex items-center gap-2 mb-4" data-id="element-610">
                  <MessageSquareIcon size={18} className="text-blue-600" data-id="element-611" />
                  <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wide" data-id="element-612">
                    QUESTION / CONVERSATION BY VOICEBOT AGENT
                  </h3>
                </div>

                <div className="space-y-4" data-id="element-613">
                  {/* Name Question */}
                  <div className="flex items-start gap-3" data-id="element-614">
                    <AIIcon data-id="element-615" />
                    <p className="text-sm text-gray-700 pt-2.5" data-id="element-616">Name?</p>
                  </div>
                  <div className="flex items-start gap-3" data-id="element-617">
                    <UserAvatar initials={userInitials} data-id="element-618" />
                    <div className="pt-2" data-id="element-619">
                      <p className="text-sm text-gray-900" data-id="element-620">{lead.name}.</p>
                      <button className="text-gray-400 hover:text-gray-600 mt-1" data-id="element-621">
                        <CopyIcon size={14} data-id="element-622" />
                      </button>
                    </div>
                  </div>

                  {/* Email Question */}
                  <div className="flex items-start gap-3" data-id="element-623">
                    <AIIcon data-id="element-624" />
                    <p className="text-sm text-gray-700 pt-2.5" data-id="element-625">Email?</p>
                  </div>
                  <div className="flex items-start gap-3" data-id="element-626">
                    <UserAvatar initials={userInitials} data-id="element-627" />
                    <div className="pt-2" data-id="element-628">
                      <p className="text-sm text-gray-900" data-id="element-629">{lead.email}</p>
                      <button className="text-gray-400 hover:text-gray-600 mt-1" data-id="element-630">
                        <CopyIcon size={14} data-id="element-631" />
                      </button>
                    </div>
                  </div>

                  {/* Model Interested Question */}
                  <div className="flex items-start gap-3" data-id="element-632">
                    <AIIcon data-id="element-633" />
                    <p className="text-sm text-gray-700 pt-2.5" data-id="element-634">
                      Model Interested?
                    </p>
                  </div>
                  <div className="flex items-start gap-3" data-id="element-635">
                    <UserAvatar initials={userInitials} data-id="element-636" />
                    <div className="pt-2" data-id="element-637">
                      <p className="text-sm text-gray-900" data-id="element-638">
                        {lead.carModel || 'Scorpio-N'}
                      </p>
                      <button className="text-gray-400 hover:text-gray-600 mt-1" data-id="element-639">
                        <CopyIcon size={14} data-id="element-640" />
                      </button>
                    </div>
                  </div>

                  {/* Test Drive Question */}
                  <div className="flex items-start gap-3" data-id="element-641">
                    <AIIcon data-id="element-642" />
                    <p className="text-sm text-gray-700 pt-2.5" data-id="element-643">Test Drive?</p>
                  </div>
                  <div className="flex items-start gap-3" data-id="element-644">
                    <UserAvatar initials={userInitials} data-id="element-645" />
                    <div className="pt-2" data-id="element-646">
                      <p className="text-sm text-gray-500 italic" data-id="element-647">
                        Not Answered...
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>}
        </div>

        {/* Footer Actions */}
        <div className="border-t border-gray-200 px-6 py-4 flex items-center justify-between bg-white" data-id="element-648">
          <button className="px-6 py-2.5 text-sm font-semibold text-blue-600 border-2 border-blue-600 rounded-lg hover:bg-blue-50 transition-colors" data-id="element-649">
            SAVE LEAD
          </button>
          <button className="px-6 py-2.5 text-sm font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2 shadow-sm" data-id="element-650">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" data-id="element-651">
              <path d="M8 2v12M2 8h12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" data-id="element-652" />
            </svg>
            SYNC & SAVE
          </button>
        </div>
      </div>
    </>;
}